import { useState, useEffect, useRef } from 'react'
import { bankDataService, type CombinedBankData } from '../../../services/bankDataService'
import { convertAndUploadJsonToCsv, type CsvUploadResponse } from '../../../services/csvDataProcessor'
import '../../admin/workflow/tabs/BanksInfoTab.css'

// Global state to prevent multiple instances from making duplicate calls
let globalFinanceBanksData: CombinedBankData | null = null
let globalFinanceIsLoading = false
let globalFinanceLoadPromise: Promise<CombinedBankData> | null = null

const BanksInfoDisplay = () => {
  const [bankData, setBankData] = useState<CombinedBankData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeDataType, setActiveDataType] = useState<'customers' | 'accounts' | 'contact_info'>('customers')
  const [exportLoading, setExportLoading] = useState<string | null>(null)
  const [exportSuccess, setExportSuccess] = useState<CsvUploadResponse | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const hasLoadedDataRef = useRef(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    // Check if we already have global data
    if (globalFinanceBanksData) {
      console.log('📋 [FinanceBanksInfo] Using cached global data')
      setBankData(globalFinanceBanksData)
      setLoading(false)
      hasLoadedDataRef.current = true
      return
    }

    // Check if already loading globally
    if (globalFinanceIsLoading) {
      console.log('⏳ [FinanceBanksInfo] Already loading globally, waiting...')
      if (globalFinanceLoadPromise) {
        globalFinanceLoadPromise.then(data => {
          setBankData(data)
          setLoading(false)
          hasLoadedDataRef.current = true
        }).catch(err => {
          setError(err instanceof Error ? err.message : 'Failed to load banks data')
          setLoading(false)
        })
      }
      return
    }

    // Prevent duplicate API calls and abort any existing requests
    if (hasLoadedDataRef.current) return

    // Cancel any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController()

    const loadBanksData = async (): Promise<CombinedBankData> => {
      try {
        setLoading(true)
        setError(null)
        globalFinanceIsLoading = true

        console.log('📡 [FinanceBanksInfo] Starting data fetch for finance banks...')

        const data = await bankDataService.getCombinedBankData()

        // Set both local and global state
        setBankData(data)
        globalFinanceBanksData = data

        console.log('✅ [FinanceBanksInfo] Finance banks data loaded successfully', {
          bank_a: data.bankA ? 'loaded' : 'failed',
          bank_b: data.bankB ? 'loaded' : 'failed'
        })

        // Mark data as loaded to prevent duplicate calls
        hasLoadedDataRef.current = true

        return data

      } catch (err) {
        // Don't set error if request was aborted
        if (err instanceof Error && err.name === 'AbortError') {
          console.log('🚫 [FinanceBanksInfo] Request was aborted')
          throw err // Re-throw to handle in promise chain
        }
        const errorMessage = err instanceof Error ? err.message : 'Failed to load banks data'
        setError(errorMessage)
        console.error('❌ [FinanceBanksInfo] Error loading banks data:', err)
        throw new Error(errorMessage)
      } finally {
        setLoading(false)
        globalFinanceIsLoading = false
        globalFinanceLoadPromise = null
      }
    }

    // Set up global promise and execute
    globalFinanceLoadPromise = loadBanksData()
    globalFinanceLoadPromise.then(() => {
      console.log('📋 [FinanceBanksInfo] Global promise resolved with data')
    }).catch(err => {
      console.error('❌ [FinanceBanksInfo] Global promise rejected:', err)
    })

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        console.log('🧹 [FinanceBanksInfo] Cleanup - aborting requests')
        abortControllerRef.current.abort()
      }
    }
  }, []) // Empty dependency array - only run once on mount

  const fetchBankData = async () => {
    // Reset global state to force refresh
    globalFinanceBanksData = null
    globalFinanceIsLoading = false
    globalFinanceLoadPromise = null
    hasLoadedDataRef.current = false

    try {
      setLoading(true)
      setError(null)
      const data = await bankDataService.getCombinedBankData()
      setBankData(data)
      globalFinanceBanksData = data
      hasLoadedDataRef.current = true
    } catch (err: any) {
      setError(err.message || 'Failed to fetch bank data')
    } finally {
      setLoading(false)
    }
  }

  // Handle data type switching
  const handleDataTypeSwitch = (dataType: 'customers' | 'accounts' | 'contact_info') => {
    setActiveDataType(dataType)
  }

  // Handle CSV export for different banks
  const handleExport = async (bankType: 'bank_a' | 'bank_b') => {
    if (!bankData) return

    try {
      setExportLoading(bankType)
      setExportError(null)
      setExportSuccess(null)

      let dataToExport: any[] = []
      let fileName = ''

      // Export ALL data from the API response (clients + contact_info + accounts)
      if (bankType === 'bank_a') {
        const bankAClients = bankData.bankA?.data.clients || []
        const bankAContacts = bankData.bankA?.data.contact_info || []
        const bankAAccounts = bankData.bankA?.data.accounts || []

        // Create one row per account with client and contact info included
        dataToExport = bankAAccounts.map(account => {
          const client = bankAClients.find(c => c.client_identifier === account.client_identifier)
          const contact = bankAContacts.find(c => c.client_identifier === account.client_identifier)

          return {
            // Client information (excluding system fields)
            client_identifier: client?.client_identifier || '',
            first_name: client?.first_name || '',
            last_name: client?.last_name || '',
            date_of_birth: client?.date_of_birth || '',
            designation: client?.designation || '',
            company: client?.company || '',
            joining_date: client?.joining_date || '',
            is_active: client?.is_active || false,
            risk_profile: client?.risk_profile || '',

            // Contact information (excluding system fields)
            email_address: contact?.email_address || '',
            emergency_contact_name: contact?.emergency_contact_name || '',
            address_line_1: contact?.address_line_1 || '',
            address_line_2: contact?.address_line_2 || '',
            street: contact?.street || '',
            city: contact?.city || '',
            state: contact?.state || '',
            country: contact?.country || '',
            mailing_zip_code: contact?.mailing_zip_code || '',
            social_security_number: contact?.social_security_number || '',
            phone_number: contact?.phone_number || '',

            // Account information (excluding system fields)
            account_identifier: account?.account_identifier || '',
            amount: account?.amount || '',
            status: account?.status || '',
            account_type: account?.account_type || '',
            account_name: account?.account_name || ''
          }
        })
        fileName = `bank_a_complete_${new Date().toISOString().split('T')[0]}`

      } else if (bankType === 'bank_b') {
        const bankBClients = bankData.bankB?.data.clients || []
        const bankBContacts = bankData.bankB?.data.contact_info || []
        const bankBAccounts = bankData.bankB?.data.accounts || []

        // Create one row per account with client and contact info included
        dataToExport = bankBAccounts.map(account => {
          const client = bankBClients.find(c => c.client_identifier === account.client_identifier)
          const contact = bankBContacts.find(c => c.client_identifier === account.client_identifier)

          return {
            // Client information (excluding system fields)
            client_identifier: client?.client_identifier || '',
            first_name: client?.first_name || '',
            last_name: client?.last_name || '',
            date_of_birth: client?.date_of_birth || '',
            designation: client?.designation || '',
            company: client?.company || '',
            joining_date: client?.joining_date || '',
            is_active: client?.is_active || false,
            risk_profile: client?.risk_profile || '',

            // Contact information (excluding system fields)
            email_address: contact?.email_address || '',
            emergency_contact_name: contact?.emergency_contact_name || '',
            address_line_1: contact?.address_line_1 || '',
            address_line_2: contact?.address_line_2 || '',
            street: contact?.street || '',
            city: contact?.city || '',
            state: contact?.state || '',
            country: contact?.country || '',
            mailing_zip_code: contact?.mailing_zip_code || '',
            social_security_number: contact?.social_security_number || '',
            phone_number: contact?.phone_number || '',

            // Account information (excluding system fields)
            account_identifier: account?.account_identifier || '',
            amount: account?.amount || '',
            status: account?.status || '',
            account_type: account?.account_type || '',
            account_name: account?.account_name || ''
          }
        })
        fileName = `bank_b_complete_${new Date().toISOString().split('T')[0]}`
      }

      if (dataToExport.length === 0) {
        throw new Error('No data available for export')
      }

      const result = await convertAndUploadJsonToCsv(dataToExport, fileName, undefined, bankType)
      setExportSuccess(result)

    } catch (err: any) {
      setExportError(err.message || 'Export failed')
    } finally {
      setExportLoading(null)
    }
  }

  // Clear export notifications
  const clearExportNotifications = () => {
    setExportSuccess(null)
    setExportError(null)
  }

  if (loading) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div
            style={{
              margin: '0 auto 16px',
              width: '32px',
              height: '32px',
              border: '3px solid #f3f4f6',
              borderTop: '3px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite'
            }}
          ></div>
          <p style={{ color: '#6b7280' }}>Loading bank data...</p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ color: '#dc2626', marginBottom: '8px' }}>Error Loading Bank Data</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>{error}</p>
          <button
            onClick={fetchBankData}
            style={{
              padding: '8px 16px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!bankData) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h3>No Bank Data Available</h3>
        </div>
      </div>
    )
  }

  if (!bankData.bankA && !bankData.bankB) {
    return (
      <div className="banks-info-container">
        <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h3>No Bank Data Available</h3>
        </div>
      </div>
    )
  }

  // Get current data based on active data type
  const bankAData = activeDataType === 'customers'
    ? (bankData.bankA?.data.clients || [])
    : activeDataType === 'accounts'
    ? (bankData.bankA?.data.accounts || [])
    : (bankData.bankA?.data.contact_info || [])
  const bankBData = activeDataType === 'customers'
    ? (bankData.bankB?.data.clients || [])
    : activeDataType === 'accounts'
    ? (bankData.bankB?.data.accounts || [])
    : (bankData.bankB?.data.contact_info || [])

  // Get all available fields from both banks for comprehensive comparison
  const getAllFieldsFromData = () => {
    const allFields = new Set<string>()

    // Add fields from main datasets (clients/accounts/contact_info)
    ;[...bankAData, ...bankBData].forEach(item => {
      if (item) {
        Object.keys(item).forEach(key => {
          // Only skip truly internal system fields - keep all business fields
          if (!['id', 'created_at', 'updated_at', 'deleted_at', 'created_by', 'updated_by', 'deleted_by'].includes(key)) {
            allFields.add(key)
          }
        })
      }
    })

    // For customer data, also add fields from contact_info to ensure comprehensive comparison
    if (activeDataType === 'customers') {
      const bankAContacts = bankData.bankA?.data.contact_info || []
      const bankBContacts = bankData.bankB?.data.contact_info || []

      ;[...bankAContacts, ...bankBContacts].forEach(contact => {
        if (contact) {
          Object.keys(contact).forEach(key => {
            // Add contact fields, excluding system fields and client_id (duplicate of client_identifier)
            if (!['id', 'client_id', 'created_at', 'updated_at', 'deleted_at', 'created_by', 'updated_by', 'deleted_by'].includes(key)) {
              allFields.add(key)
            }
          })
        }
      })
    }

    // Convert to array and sort for consistent ordering
    // Prioritize important business fields first
    const fieldsArray = Array.from(allFields)
    const priorityFields = [
      'client_identifier', 'first_name', 'last_name', 'designation', 'company',
      'date_of_birth', 'joining_date', 'is_active', 'risk_profile',
      'email_address', 'phone_number', 'emergency_contact_name',
      'address_line_1', 'address_line_2', 'street', 'city', 'state', 'country', 'mailing_zip_code',
      'account_identifier', 'account_type', 'account_name', 'amount', 'status'
    ]

    const priorityFieldsInData = priorityFields.filter(field => fieldsArray.includes(field))
    const remainingFields = fieldsArray.filter(field => !priorityFields.includes(field)).sort()

    return [...priorityFieldsInData, ...remainingFields]
  }

  // Format field name for display
  const formatFieldName = (fieldName: string): string => {
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  // Format field value for display
  const formatFieldValue = (value: any, fieldName: string): string => {
    if (value === null || value === undefined || value === '') {
      return 'N/A'
    }

    // Special formatting for specific field types
    if (fieldName === 'amount' && typeof value === 'string') {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        return `$${numValue.toLocaleString()}`
      }
    }

    if (fieldName === 'is_active' && typeof value === 'boolean') {
      return value ? 'Active' : 'Inactive'
    }

    if (fieldName === 'social_security_number' && typeof value === 'string') {
      return value.length > 4 ? '***-**-' + value.slice(-4) : value
    }

    // Format dates nicely
    if ((fieldName.includes('date') || fieldName.includes('_at')) && typeof value === 'string') {
      try {
        const date = new Date(value)
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })
        }
      } catch (e) {
        // If date parsing fails, return original value
      }
    }

    // Format boolean values
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No'
    }

    // Handle arrays (in case any fields contain arrays)
    if (Array.isArray(value)) {
      return value.length > 0 ? value.join(', ') : 'Empty'
    }

    // Handle objects (stringify them)
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value)
    }

    return String(value)
  }

  // Get comprehensive row data for comparison
  const getComprehensiveRowData = (bankAItem: any, bankBItem: any) => {
    const allFields = getAllFieldsFromData()

    // For contact info, we need to merge data
    let bankAContactInfo = null
    let bankBContactInfo = null

    if (activeDataType === 'customers') {
      bankAContactInfo = bankData.bankA?.data.contact_info?.find(
        (contact: any) => contact.client_identifier === bankAItem?.client_identifier
      )
      bankBContactInfo = bankData.bankB?.data.contact_info?.find(
        (contact: any) => contact.client_identifier === bankBItem?.client_identifier
      )
    }

    return allFields.map(field => {
      let bankAValue = bankAItem?.[field as keyof typeof bankAItem]
      let bankBValue = bankBItem?.[field as keyof typeof bankBItem]

      // For customer data, check contact info if field not found in main record
      if (activeDataType === 'customers') {
        if (bankAValue === undefined && bankAContactInfo && field in bankAContactInfo) {
          bankAValue = (bankAContactInfo as any)[field]
        }
        if (bankBValue === undefined && bankBContactInfo && field in bankBContactInfo) {
          bankBValue = (bankBContactInfo as any)[field]
        }
      }

      const formattedBankAValue = formatFieldValue(bankAValue, field)
      const formattedBankBValue = formatFieldValue(bankBValue, field)

      return {
        field,
        displayName: formatFieldName(field),
        bankAValue: formattedBankAValue,
        bankBValue: formattedBankBValue,
        isDifferent: formattedBankAValue !== formattedBankBValue &&
                    formattedBankAValue !== 'N/A' &&
                    formattedBankBValue !== 'N/A'
      }
    })
  }

  return (
    <div className="banks-info-container">
      {/* Header */}
      <div className="banks-info-header">
        <div className="banks-info-header-left">
          <h2 className="banks-info-title">Banks Comparison</h2>
          <p className="banks-info-subtitle">
            Side-by-side comparison of Bank A and Bank B data
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => handleExport('bank_a')}
            disabled={exportLoading === 'bank_a'}
            className="data-type-btn"
            style={{
              backgroundColor: exportLoading === 'bank_a' ? '#f3f4f6' : '#3b82f6',
              color: exportLoading === 'bank_a' ? '#6b7280' : 'white',
              cursor: exportLoading === 'bank_a' ? 'not-allowed' : 'pointer',
              opacity: exportLoading === 'bank_a' ? 0.6 : 1
            }}
          >
            {exportLoading === 'bank_a' ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #6b7280',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }}></div>
                Exporting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export Bank A
              </>
            )}
          </button>

          <button
            onClick={() => handleExport('bank_b')}
            disabled={exportLoading === 'bank_b'}
            className="data-type-btn"
            style={{
              backgroundColor: exportLoading === 'bank_b' ? '#f3f4f6' : '#10b981',
              color: exportLoading === 'bank_b' ? '#6b7280' : 'white',
              cursor: exportLoading === 'bank_b' ? 'not-allowed' : 'pointer',
              opacity: exportLoading === 'bank_b' ? 0.6 : 1
            }}
          >
            {exportLoading === 'bank_b' ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  border: '2px solid #6b7280',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  marginRight: '6px'
                }}></div>
                Exporting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '6px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7,10 12,15 17,10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export Bank B
              </>
            )}
          </button>
        </div>
      </div>

      {/* Data Type Selector */}
      <div className="data-type-selector">
        <button
          className={`data-type-btn ${activeDataType === 'customers' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('customers')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          Customer Data
        </button>
        <button
          className={`data-type-btn ${activeDataType === 'accounts' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('accounts')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="1" y="3" width="15" height="13"/>
            <path d="M16 8h4a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2h-4"/>
            <circle cx="9" cy="9" r="2"/>
          </svg>
          Account Data
        </button>
        {/* <button
          className={`data-type-btn ${activeDataType === 'contact_info' ? 'active' : ''}`}
          onClick={() => handleDataTypeSwitch('contact_info')}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
          Contact Data
        </button> */}
      </div>

      {/* Export Success/Error Messages */}
      {(exportSuccess || exportError) && (
        <div style={{ marginBottom: '24px' }}>
          {exportSuccess && (
            <div style={{
              padding: '12px',
              backgroundColor: '#f0f9ff',
              border: '1px solid #0ea5e9',
              borderRadius: '6px',
              position: 'relative'
            }}>
              <button
                onClick={clearExportNotifications}
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  color: '#6b7280',
                  backgroundColor: 'transparent',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#0369a1', margin: '0 0 8px 0' }}>
                ✅ CSV Export Successful!
              </h4>
              <p style={{ fontSize: '12px', color: '#0369a1', margin: '0 0 8px 0' }}>
                {exportSuccess.message}
              </p>
              <div style={{ fontSize: '11px', color: '#0284c7' }}>
                <div style={{ marginBottom: '4px' }}>
                  <strong>File:</strong> {exportSuccess.data.original_filename}
                </div>
                <div style={{ marginBottom: '4px' }}>
                  <strong>Size:</strong> {(exportSuccess.data.file_size / 1024).toFixed(1)} KB
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <strong>S3 URL:</strong>
                  <code style={{
                    fontSize: '10px',
                    backgroundColor: '#e0f2fe',
                    padding: '2px 4px',
                    borderRadius: '3px',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {exportSuccess.data.s3_url}
                  </code>
                  <button
                    onClick={() => navigator.clipboard.writeText(exportSuccess.data.s3_url)}
                    style={{
                      padding: '2px 4px',
                      fontSize: '10px',
                      backgroundColor: '#0ea5e9',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer'
                    }}
                    title="Copy S3 URL"
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Export Error Message */}
          {exportError && (
            <div style={{
              padding: '12px',
              backgroundColor: '#fef2f2',
              border: '1px solid #f87171',
              borderRadius: '6px'
            }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#dc2626', margin: '0 0 4px 0' }}>
                ❌ Export Failed
              </h4>
              <p style={{ fontSize: '12px', color: '#dc2626', margin: 0 }}>
                {exportError}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Side-by-Side Comparison Table */}
      <div className="comparison-section">
        <h3 className="section-title">
          {activeDataType === 'customers' ? 'Customer Data' : activeDataType === 'accounts' ? 'Account Data' : 'Contact Information'} Comparison
        </h3>


        <div className="comparison-table-container">
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th className="record-header">#</th>
                  <th className="field-header">Field</th>
                  <th className="bank-header bank-a-header">Bank A</th>
                  <th className="bank-header bank-b-header">Bank B</th>
                </tr>
              </thead>
              <tbody>
                {/* Show first 10 records for comparison with comprehensive field comparison */}
                {Array.from({ length: Math.min(Math.max(bankAData.length, bankBData.length), 10) }).map((_, recordIndex) => {
                  const bankAItem = bankAData[recordIndex]
                  const bankBItem = bankBData[recordIndex]

                  if (!bankAItem && !bankBItem) return null

                  const comprehensiveRowData = getComprehensiveRowData(bankAItem, bankBItem)

                  return comprehensiveRowData.map((fieldData, fieldIndex) => (
                    <tr key={`${recordIndex}-${fieldIndex}`} className={`record-row ${recordIndex % 2 === 0 ? 'even-record' : 'odd-record'} ${fieldIndex === 0 && recordIndex > 0 ? 'record-first-row' : ''}`}>
                      {fieldIndex === 0 && (
                        <td rowSpan={comprehensiveRowData.length} className="record-number">
                          {recordIndex + 1}
                        </td>
                      )}
                      <td className="field-name">
                        {fieldData.displayName}
                      </td>
                      <td className={`data-cell bank-a-data ${!bankAItem ? 'no-data' : ''} ${fieldData.isDifferent ? 'different-value' : ''}`}>
                        {bankAItem ? fieldData.bankAValue : <span className="no-data-dash">—</span>}
                      </td>
                      <td className={`data-cell bank-b-data ${!bankBItem ? 'no-data' : ''} ${fieldData.isDifferent ? 'different-value' : ''}`}>
                        {bankBItem ? fieldData.bankBValue : <span className="no-data-dash">—</span>}
                      </td>
                    </tr>
                  ))
                }).flat().filter(Boolean)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="comparison-footer">
          <p>Showing first 10 records for comparison. Different values are highlighted with colored backgrounds.</p>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          tr.even-record td { background-color: #f8fafc; }
          tr.odd-record td  { background-color: #ffffff; }

          td.bank-a-data.different-value { background-color: #fef2f2 !important; color: #dc2626 !important; font-weight: 500; }
          td.bank-b-data.different-value { background-color: #eff6ff !important; color: #1e40af !important; font-weight: 500; }

          /* Separator line between record groups */
          tr.record-first-row td {
            border-top: 2px solid #a6a8ab !important;
          }

          td.record-number {
            vertical-align: top;
            font-weight: 700;
            text-align: center;
            font-size: 12px;
            min-width: 32px;
          }
        `}
      </style>
    </div>
  )
}

export default BanksInfoDisplay